import { DynamicHoverData, HoverConfig } from '../../types/dynamicHover';
import { TicketContext } from '../../utils/ticketContextDetector';

interface Answer {
  question: {
    questionTranslationKey: string;
    inputLabelTranslationKey?: string;
    options?: Array<{
      id: string;
      translationKey: string;
    }>;
  };
  answer: string;
  selectedOption?: {
    translationKey: string;
  };
}

function getTranslationValueFromAnswer(answer: Answer): string {
  if (answer.selectedOption?.translationKey) {
    return answer.selectedOption.translationKey;
  }

  if (
    answer.question.options &&
    answer.answer &&
    answer.answer.match(
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    )
  ) {
    const option = answer.question.options.find(
      opt => opt.id === answer.answer
    );
    if (option?.translationKey) {
      return option.translationKey;
    }
  }

  return answer.answer || 'N/A';
}

const GIFTS_AND_DONATIONS_CONFIG: HoverConfig = {
  commonFields: [
    'recipientDetails.typeOfRecipient',
    'paymentDetails.totalAmountInUSD',
  ],
  subcategorySpecificFields: {
    giftOrHospitality: ['giftOrHospitalityDetails.typeOfGiftOrHospitality'],
    donation: ['donationDetails.typeOfContribution'],
    sponsorship: ['sponsorshipDetails.typeOfSponsorship'],
    politicalContribution: ['recipientDetails.typeOfContribution'],
  },
};

const GIFTS_AND_DONATIONS_RECEIVING_CONFIG: HoverConfig = {
  commonFields: [
    'individualOrEntityDetails.typeOfIndividualOrEntityProvidingTheGiftOrHospitality',
    'giftOrHospitalityDetails.typeOfGiftOrHospitality',
    'paymentDetails.totalAmountInUSD',
  ],
  subcategorySpecificFields: {},
};

const GIFTS_AND_DONATIONS_OFFICE_INTERNAL_CONFIG: HoverConfig = {
  commonFields: [
    'eventDetails.dateOfTheEvent',
    'paymentDetails.totalAmountInUSD',
  ],
  subcategorySpecificFields: {},
};

function extractDescriptionFromAnswers(
  answers: Answer[],
  subcategory: string,
  category: string
): string {
  if (category === 'youAreReceiving') {
    const descriptionAnswer = answers.find(answer =>
      answer.question.questionTranslationKey.includes(
        '.giftOrHospitalityDetails.detailedDescriptionOfTheGiftOrHospitalityProvided'
      )
    );
    return descriptionAnswer
      ? getTranslationValueFromAnswer(descriptionAnswer)
      : 'N/A';
  }

  if (category === 'officeInternalUsage') {
    const descriptionAnswer = answers.find(answer =>
      answer.question.questionTranslationKey.includes(
        '.eventDetails.detailedDescriptionOfTheEventProvided'
      )
    );
    return descriptionAnswer
      ? getTranslationValueFromAnswer(descriptionAnswer)
      : 'N/A';
  }

  if (subcategory === 'giftOrHospitality') {
    const descriptionAnswer = answers.find(answer =>
      answer.question.questionTranslationKey.includes(
        '.giftOrHospitalityDetails.detailedDescriptionOfTheGiftOrHospitalityProvided'
      )
    );
    return descriptionAnswer
      ? getTranslationValueFromAnswer(descriptionAnswer)
      : 'N/A';
  }
  if (subcategory === 'donation') {
    const descriptionAnswer = answers.find(answer =>
      answer.question.questionTranslationKey.includes(
        '.donationDetails.detailedDescriptionOfTheDonationProvided'
      )
    );
    return descriptionAnswer
      ? getTranslationValueFromAnswer(descriptionAnswer)
      : 'N/A';
  }
  if (subcategory === 'sponsorship') {
    const descriptionAnswer = answers.find(answer =>
      answer.question.questionTranslationKey.includes(
        '.sponsorshipDetails.detailedDescriptionOfTheSponsorshipProvided'
      )
    );
    return descriptionAnswer
      ? getTranslationValueFromAnswer(descriptionAnswer)
      : 'N/A';
  }
  if (subcategory === 'politicalContribution') {
    const descriptionAnswer = answers.find(answer =>
      answer.question.questionTranslationKey.includes(
        '.paymentDetails.costCenter'
      )
    );
    return descriptionAnswer
      ? getTranslationValueFromAnswer(descriptionAnswer)
      : 'N/A';
  }

  return 'N/A';
}

export function mapGiftsAndDonationsHoverData(
  answers: Answer[],
  context: TicketContext
): DynamicHoverData {
  let config: HoverConfig;
  if (context.category === 'youAreReceiving') {
    config = GIFTS_AND_DONATIONS_RECEIVING_CONFIG;
  } else if (context.category === 'officeInternalUsage') {
    config = GIFTS_AND_DONATIONS_OFFICE_INTERNAL_CONFIG;
  } else {
    config = GIFTS_AND_DONATIONS_CONFIG;
  }

  const leftBoxFields = [];

  if (context.category === 'youAreReceiving') {
    const typeAnswer = answers.find(answer =>
      answer.question.questionTranslationKey.includes(
        '.individualOrEntityDetails.typeOfIndividualOrEntityProvidingTheGiftOrHospitality'
      )
    );
    const giftTypeAnswer = answers.find(answer =>
      answer.question.questionTranslationKey.includes(
        '.giftOrHospitalityDetails.typeOfGiftOrHospitality'
      )
    );
    const amountAnswer = answers.find(answer =>
      answer.question.questionTranslationKey.includes(
        '.paymentDetails.totalAmountInUSD'
      )
    );

    if (typeAnswer) {
      leftBoxFields.push({
        label:
          typeAnswer.question.inputLabelTranslationKey ||
          typeAnswer.question.questionTranslationKey,
        value: getTranslationValueFromAnswer(typeAnswer),
        type: 'text' as const,
      });
    }
    if (giftTypeAnswer) {
      leftBoxFields.push({
        label:
          giftTypeAnswer.question.inputLabelTranslationKey ||
          giftTypeAnswer.question.questionTranslationKey,
        value: getTranslationValueFromAnswer(giftTypeAnswer),
        type: 'text' as const,
      });
    }
    if (amountAnswer) {
      leftBoxFields.push({
        label:
          amountAnswer.question.inputLabelTranslationKey ||
          amountAnswer.question.questionTranslationKey,
        value: getTranslationValueFromAnswer(amountAnswer),
        type: 'text' as const,
      });
    }
  } else if (context.category === 'officeInternalUsage') {
    const eventDateAnswer = answers.find(answer =>
      answer.question.questionTranslationKey.includes(
        '.eventDetails.dateOfTheEvent'
      )
    );
    const amountAnswer = answers.find(answer =>
      answer.question.questionTranslationKey.includes(
        '.paymentDetails.totalAmountInUSD'
      )
    );

    if (eventDateAnswer) {
      leftBoxFields.push({
        label:
          eventDateAnswer.question.inputLabelTranslationKey ||
          eventDateAnswer.question.questionTranslationKey,
        value: getTranslationValueFromAnswer(eventDateAnswer),
        type: 'text' as const,
      });
    }
    if (amountAnswer) {
      leftBoxFields.push({
        label:
          amountAnswer.question.inputLabelTranslationKey ||
          amountAnswer.question.questionTranslationKey,
        value: getTranslationValueFromAnswer(amountAnswer),
        type: 'text' as const,
      });
    }
  } else {
    const recipientTypeAnswer = answers.find(answer =>
      answer.question.questionTranslationKey.includes(
        '.recipientDetails.typeOfRecipient'
      )
    );
    const amountAnswer = answers.find(answer =>
      answer.question.questionTranslationKey.includes(
        '.paymentDetails.totalAmountInUSD'
      )
    );

    if (recipientTypeAnswer) {
      leftBoxFields.push({
        label:
          recipientTypeAnswer.question.inputLabelTranslationKey ||
          recipientTypeAnswer.question.questionTranslationKey,
        value: getTranslationValueFromAnswer(recipientTypeAnswer),
        type: 'text' as const,
      });
    }
    if (amountAnswer) {
      leftBoxFields.push({
        label:
          amountAnswer.question.inputLabelTranslationKey ||
          amountAnswer.question.questionTranslationKey,
        value: getTranslationValueFromAnswer(amountAnswer),
        type: 'text' as const,
      });
    }

    if (context.subcategory === 'giftOrHospitality') {
      const giftTypeAnswer = answers.find(answer =>
        answer.question.questionTranslationKey.includes(
          '.giftOrHospitalityDetails.typeOfGiftOrHospitality'
        )
      );
      if (giftTypeAnswer) {
        leftBoxFields.push({
          label:
            giftTypeAnswer.question.inputLabelTranslationKey ||
            giftTypeAnswer.question.questionTranslationKey,
          value: getTranslationValueFromAnswer(giftTypeAnswer),
          type: 'text' as const,
        });
      }
    } else if (context.subcategory === 'donation') {
      const contributionTypeAnswer = answers.find(answer =>
        answer.question.questionTranslationKey.includes(
          '.donationDetails.typeOfContribution'
        )
      );
      if (contributionTypeAnswer) {
        leftBoxFields.push({
          label:
            contributionTypeAnswer.question.inputLabelTranslationKey ||
            contributionTypeAnswer.question.questionTranslationKey,
          value: getTranslationValueFromAnswer(contributionTypeAnswer),
          type: 'text' as const,
        });
      }
    } else if (context.subcategory === 'sponsorship') {
      const sponsorshipTypeAnswer = answers.find(answer =>
        answer.question.questionTranslationKey.includes(
          '.sponsorshipDetails.typeOfSponsorship'
        )
      );
      if (sponsorshipTypeAnswer) {
        leftBoxFields.push({
          label:
            sponsorshipTypeAnswer.question.inputLabelTranslationKey ||
            sponsorshipTypeAnswer.question.questionTranslationKey,
          value: getTranslationValueFromAnswer(sponsorshipTypeAnswer),
          type: 'text' as const,
        });
      }
    } else if (context.subcategory === 'politicalContribution') {
      const contributionTypeAnswer = answers.find(answer =>
        answer.question.questionTranslationKey.includes(
          '.recipientDetails.typeOfContribution'
        )
      );
      if (contributionTypeAnswer) {
        leftBoxFields.push({
          label:
            contributionTypeAnswer.question.inputLabelTranslationKey ||
            contributionTypeAnswer.question.questionTranslationKey,
          value: getTranslationValueFromAnswer(contributionTypeAnswer),
          type: 'text' as const,
        });
      }
    }
  }

  const actualDescription = extractDescriptionFromAnswers(
    answers,
    context.subcategory,
    context.category
  );

  let descriptionQuestionKey = '';
  if (context.category === 'youAreReceiving') {
    const descriptionAnswer = answers.find(answer =>
      answer.question.questionTranslationKey.includes(
        '.giftOrHospitalityDetails.detailedDescriptionOfTheGiftOrHospitalityProvided'
      )
    );
    descriptionQuestionKey =
      descriptionAnswer?.question.inputLabelTranslationKey ||
      descriptionAnswer?.question.questionTranslationKey ||
      'Description';
  } else if (context.category === 'officeInternalUsage') {
    const descriptionAnswer = answers.find(answer =>
      answer.question.questionTranslationKey.includes(
        '.eventDetails.detailedDescriptionOfTheEventProvided'
      )
    );
    descriptionQuestionKey =
      descriptionAnswer?.question.inputLabelTranslationKey ||
      descriptionAnswer?.question.questionTranslationKey ||
      'Description';
  } else if (context.subcategory === 'giftOrHospitality') {
    const descriptionAnswer = answers.find(answer =>
      answer.question.questionTranslationKey.includes(
        '.giftOrHospitalityDetails.detailedDescriptionOfTheGiftOrHospitalityProvided'
      )
    );
    descriptionQuestionKey =
      descriptionAnswer?.question.inputLabelTranslationKey ||
      descriptionAnswer?.question.questionTranslationKey ||
      'Description';
  } else if (context.subcategory === 'donation') {
    const descriptionAnswer = answers.find(answer =>
      answer.question.questionTranslationKey.includes(
        '.donationDetails.detailedDescriptionOfTheDonationProvided'
      )
    );
    descriptionQuestionKey =
      descriptionAnswer?.question.inputLabelTranslationKey ||
      descriptionAnswer?.question.questionTranslationKey ||
      'Description';
  } else if (context.subcategory === 'sponsorship') {
    const descriptionAnswer = answers.find(answer =>
      answer.question.questionTranslationKey.includes(
        '.sponsorshipDetails.detailedDescriptionOfTheSponsorshipProvided'
      )
    );
    descriptionQuestionKey =
      descriptionAnswer?.question.inputLabelTranslationKey ||
      descriptionAnswer?.question.questionTranslationKey ||
      'Description';
  } else if (context.subcategory === 'politicalContribution') {
    const descriptionAnswer = answers.find(answer =>
      answer.question.questionTranslationKey.includes(
        '.paymentDetails.costCenter'
      )
    );
    descriptionQuestionKey =
      descriptionAnswer?.question.inputLabelTranslationKey ||
      descriptionAnswer?.question.questionTranslationKey ||
      'Description';
  }

  return {
    formType: context.formType,
    category: context.category,
    subcategory: context.subcategory,
    boxes: [
      {
        title: 'form.myTickets.title.details',
        fields: leftBoxFields,
      },
      {
        title: 'form.myTickets.title.description',
        fields: [
          {
            label: descriptionQuestionKey,
            value: actualDescription,
            type: 'text' as const,
          },
        ],
      },
    ],
  };
}

export function isGiftsAndDonationsYouAreGiving(
  context: TicketContext
): boolean {
  return (
    context.formType === 'giftsAndDonations' &&
    context.category === 'youAreGiving'
  );
}

export function isGiftsAndDonationsYouAreReceiving(
  context: TicketContext
): boolean {
  return (
    context.formType === 'giftsAndDonations' &&
    context.category === 'youAreReceiving'
  );
}

export function isGiftsAndDonationsOfficeInternalUsage(
  context: TicketContext
): boolean {
  return (
    context.formType === 'giftsAndDonations' &&
    context.category === 'officeInternalUsage'
  );
}
